<template>
  <div class="smart-incident-form">
    <FormValidation
      v-model="formData"
      :rules="validationRules"
      :show-validation-summary="false"
      :validate-on-change="true"
      @validation-change="onValidationChange"
      ref="formValidator"
    >
      <template #default="{ errors, hasError, setFieldValue, getFieldValue }">
        <v-form ref="vForm" @submit.prevent="handleSubmit">
          <!-- Header del formulario -->
          <div class="form-header mb-6">
            <div class="d-flex align-center mb-4">
              <v-icon size="32" color="error" class="mr-3">
                mdi-alert-circle-outline
              </v-icon>
              <div>
                <h2 class="text-h5 font-weight-bold">
                  {{ isEdit ? 'Editar Incidencia' : 'Nueva Incidencia' }}
                </h2>
                <p class="text-body-2 text-grey mb-0">
                  {{ isEdit ? 'Actualiza los detalles de la incidencia' : 'Reporta una nueva incidencia en el sistema' }}
                </p>
              </div>
            </div>
            
            <!-- Progress indicator -->
            <v-progress-linear
              :model-value="formProgress"
              height="4"
              color="primary"
              rounded
            ></v-progress-linear>
          </div>

          <v-row>
            <!-- Información básica -->
            <v-col cols="12">
              <v-card class="mb-4" elevation="2">
                <v-card-title class="bg-primary text-white">
                  <v-icon class="mr-2">mdi-information</v-icon>
                  Información Básica
                </v-card-title>
                <v-card-text class="pa-6">
                  <v-row>
                    <!-- Título -->
                    <v-col cols="12" md="6">
                      <v-text-field
                        :model-value="getFieldValue('title')"
                        @update:model-value="(val) => setFieldValue('title', val)"
                        label="Título de la incidencia *"
                        placeholder="Ej: Falla de internet en Agencia Norte"
                        variant="outlined"
                        :error="hasError('title')"
                        :error-messages="errors.title"
                        prepend-inner-icon="mdi-format-title"
                        counter="100"
                        maxlength="100"
                        @blur="markTouched('title')"
                      ></v-text-field>
                    </v-col>

                    <!-- Prioridad -->
                    <v-col cols="12" md="6">
                      <v-select
                        :model-value="getFieldValue('priority')"
                        @update:model-value="(val) => setFieldValue('priority', val)"
                        :items="priorityOptions"
                        label="Prioridad *"
                        variant="outlined"
                        :error="hasError('priority')"
                        :error-messages="errors.priority"
                        prepend-inner-icon="mdi-priority-high"
                        @blur="markTouched('priority')"
                      >
                        <template #item="{ props, item }">
                          <v-list-item v-bind="props" :title="item.title">
                            <template #prepend>
                              <v-chip
                                :color="item.raw.color"
                                size="small"
                                variant="flat"
                              >
                                {{ item.title }}
                              </v-chip>
                            </template>
                          </v-list-item>
                        </template>
                        <template #selection="{ item }">
                          <v-chip
                            :color="item.raw.color"
                            size="small"
                            variant="flat"
                          >
                            {{ item.title }}
                          </v-chip>
                        </template>
                      </v-select>
                    </v-col>

                    <!-- Categoría con autocompletado inteligente -->
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        :model-value="getFieldValue('category')"
                        @update:model-value="(val) => setFieldValue('category', val)"
                        :items="filteredCategories"
                        label="Categoría *"
                        placeholder="Buscar o seleccionar categoría..."
                        variant="outlined"
                        :error="hasError('category')"
                        :error-messages="errors.category"
                        prepend-inner-icon="mdi-shape"
                        :loading="loadingCategories"
                        clearable
                        @blur="markTouched('category')"
                        @update:search="onCategorySearch"
                      >
                        <template #item="{ props, item }">
                          <v-list-item v-bind="props" :title="item.title">
                            <template #prepend>
                              <v-icon :color="item.raw.color">
                                {{ item.raw.icon }}
                              </v-icon>
                            </template>
                          </v-list-item>
                        </template>
                      </v-autocomplete>
                    </v-col>

                    <!-- Agencia con búsqueda inteligente -->
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        :model-value="getFieldValue('agencyId')"
                        @update:model-value="(val) => setFieldValue('agencyId', val)"
                        :items="filteredAgencies"
                        label="Agencia afectada *"
                        placeholder="Buscar agencia por nombre o código..."
                        variant="outlined"
                        :error="hasError('agencyId')"
                        :error-messages="errors.agencyId"
                        prepend-inner-icon="mdi-store"
                        :loading="loadingAgencies"
                        clearable
                        @blur="markTouched('agencyId')"
                        @update:search="onAgencySearch"
                      >
                        <template #item="{ props, item }">
                          <v-list-item v-bind="props">
                            <template #title>
                              <div class="d-flex justify-space-between align-center">
                                <span>{{ item.title }}</span>
                                <v-chip size="x-small" variant="outlined">
                                  {{ item.raw.code }}
                                </v-chip>
                              </div>
                            </template>
                            <template #subtitle>
                              <span class="text-caption">{{ item.raw.address }}</span>
                            </template>
                          </v-list-item>
                        </template>
                      </v-autocomplete>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Descripción y detalles -->
            <v-col cols="12">
              <v-card class="mb-4" elevation="2">
                <v-card-title class="bg-info text-white">
                  <v-icon class="mr-2">mdi-text-box</v-icon>
                  Descripción y Detalles
                </v-card-title>
                <v-card-text class="pa-6">
                  <v-row>
                    <!-- Descripción -->
                    <v-col cols="12">
                      <v-textarea
                        :model-value="getFieldValue('description')"
                        @update:model-value="(val) => setFieldValue('description', val)"
                        label="Descripción detallada *"
                        placeholder="Describe la incidencia con el mayor detalle posible..."
                        variant="outlined"
                        :error="hasError('description')"
                        :error-messages="errors.description"
                        rows="4"
                        counter="500"
                        maxlength="500"
                        auto-grow
                        @blur="markTouched('description')"
                      >
                        <template #append-inner>
                          <v-btn
                            icon="mdi-microphone"
                            size="small"
                            variant="text"
                            @click="startVoiceInput"
                            :disabled="!voiceSupported"
                            title="Dictar descripción"
                          ></v-btn>
                        </template>
                      </v-textarea>
                    </v-col>

                    <!-- Campos condicionales basados en categoría -->
                    <template v-if="showConditionalFields">
                      <!-- Campos específicos para tecnología -->
                      <template v-if="getFieldValue('category') === 'TECNOLOGIA'">
                        <v-col cols="12" md="6">
                          <v-select
                            :model-value="getFieldValue('techIssueType')"
                            @update:model-value="(val) => setFieldValue('techIssueType', val)"
                            :items="techIssueTypes"
                            label="Tipo de problema técnico"
                            variant="outlined"
                            prepend-inner-icon="mdi-laptop"
                          ></v-select>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-text-field
                            :model-value="getFieldValue('affectedEquipment')"
                            @update:model-value="(val) => setFieldValue('affectedEquipment', val)"
                            label="Equipo afectado"
                            placeholder="Ej: PC-001, Impresora-Samsung-02"
                            variant="outlined"
                            prepend-inner-icon="mdi-desktop-classic"
                          ></v-text-field>
                        </v-col>
                      </template>

                      <!-- Campos específicos para personal -->
                      <template v-if="getFieldValue('category') === 'PERSONAL'">
                        <v-col cols="12" md="6">
                          <v-select
                            :model-value="getFieldValue('personalIssueType')"
                            @update:model-value="(val) => setFieldValue('personalIssueType', val)"
                            :items="personalIssueTypes"
                            label="Tipo de problema de personal"
                            variant="outlined"
                            prepend-inner-icon="mdi-account-group"
                          ></v-select>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-text-field
                            :model-value="getFieldValue('employeeName')"
                            @update:model-value="(val) => setFieldValue('employeeName', val)"
                            label="Nombre del empleado"
                            variant="outlined"
                            prepend-inner-icon="mdi-account"
                          ></v-text-field>
                        </v-col>
                      </template>
                    </template>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Archivos adjuntos -->
            <v-col cols="12">
              <v-card class="mb-4" elevation="2">
                <v-card-title class="bg-warning text-white">
                  <v-icon class="mr-2">mdi-paperclip</v-icon>
                  Archivos Adjuntos
                </v-card-title>
                <v-card-text class="pa-6">
                  <!-- Zona de drop de archivos -->
                  <div
                    class="file-drop-zone"
                    :class="{ 'dragover': isDragOver }"
                    @dragover.prevent="isDragOver = true"
                    @dragleave.prevent="isDragOver = false"
                    @drop.prevent="onFileDrop"
                    @click="$refs.fileInput?.click()"
                  >
                    <v-icon size="48" color="grey-lighten-1">mdi-cloud-upload</v-icon>
                    <p class="text-body-1 mt-2 mb-1">
                      Arrastra archivos aquí o haz clic para seleccionar
                    </p>
                    <p class="text-caption text-grey">
                      Imágenes, documentos PDF, hasta 10MB por archivo
                    </p>
                  </div>

                  <input
                    ref="fileInput"
                    type="file"
                    multiple
                    accept="image/*,.pdf,.doc,.docx"
                    style="display: none"
                    @change="onFileSelect"
                  />

                  <!-- Lista de archivos seleccionados -->
                  <div v-if="selectedFiles.length > 0" class="mt-4">
                    <v-divider class="mb-3"></v-divider>
                    <h4 class="text-subtitle-1 mb-3">Archivos seleccionados:</h4>
                    <v-row>
                      <v-col
                        v-for="(file, index) in selectedFiles"
                        :key="index"
                        cols="12"
                        sm="6"
                        md="4"
                      >
                        <v-card variant="outlined">
                          <v-card-text class="pa-3">
                            <div class="d-flex align-center">
                              <v-icon
                                :color="getFileIconColor(file.type)"
                                class="mr-3"
                              >
                                {{ getFileIcon(file.type) }}
                              </v-icon>
                              <div class="flex-grow-1">
                                <div class="text-caption font-weight-medium">
                                  {{ file.name }}
                                </div>
                                <div class="text-caption text-grey">
                                  {{ formatFileSize(file.size) }}
                                </div>
                              </div>
                              <v-btn
                                icon="mdi-close"
                                size="x-small"
                                variant="text"
                                @click="removeFile(index)"
                              ></v-btn>
                            </div>
                          </v-card-text>
                        </v-card>
                      </v-col>
                    </v-row>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- Acciones del formulario -->
          <div class="form-actions mt-6">
            <v-row>
              <v-col cols="12" class="text-center">
                <v-btn
                  variant="outlined"
                  size="large"
                  class="mr-4"
                  @click="handleCancel"
                  :disabled="isSubmitting"
                >
                  Cancelar
                </v-btn>
                
                <v-btn
                  v-if="!isEdit"
                  variant="outlined"
                  size="large"
                  class="mr-4"
                  @click="saveAsDraft"
                  :disabled="isSubmitting"
                  :loading="isDraftSaving"
                >
                  Guardar Borrador
                </v-btn>
                
                <v-btn
                  type="submit"
                  color="primary"
                  size="large"
                  :disabled="!isFormValid || isSubmitting"
                  :loading="isSubmitting"
                >
                  {{ isEdit ? 'Actualizar Incidencia' : 'Crear Incidencia' }}
                </v-btn>
              </v-col>
            </v-row>
          </div>
        </v-form>
      </template>
    </FormValidation>

    <!-- Auto-save indicator -->
    <v-snackbar
      v-model="showAutoSaveIndicator"
      timeout="2000"
      color="info"
      location="bottom right"
    >
      <v-icon class="mr-2">mdi-content-save</v-icon>
      Borrador guardado automáticamente
    </v-snackbar>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import FormValidation from './FormValidation.vue'

interface IncidentFormData {
  title: string
  priority: string
  category: string
  agencyId: string
  description: string
  techIssueType?: string
  affectedEquipment?: string
  personalIssueType?: string
  employeeName?: string
  files?: File[]
}

interface SmartIncidentFormProps {
  modelValue?: IncidentFormData
  isEdit?: boolean
  agencies?: Array<any>
  autoSave?: boolean
  autoSaveInterval?: number
}

interface SmartIncidentFormEmits {
  (e: 'update:modelValue', value: IncidentFormData): void
  (e: 'submit', data: IncidentFormData): void
  (e: 'cancel'): void
  (e: 'save-draft', data: IncidentFormData): void
}

const props = withDefaults(defineProps<SmartIncidentFormProps>(), {
  modelValue: () => ({
    title: '',
    priority: '',
    category: '',
    agencyId: '',
    description: ''
  }),
  isEdit: false,
  agencies: () => [],
  autoSave: true,
  autoSaveInterval: 30000 // 30 segundos
})

const emit = defineEmits<SmartIncidentFormEmits>()

// Referencias
const formValidator = ref()
const vForm = ref()
const fileInput = ref()

// Estado reactivo
const formData = ref<IncidentFormData>({ ...props.modelValue })
const isFormValid = ref(false)
const isSubmitting = ref(false)
const isDraftSaving = ref(false)
const showAutoSaveIndicator = ref(false)
const isDragOver = ref(false)
const selectedFiles = ref<File[]>([])
const loadingCategories = ref(false)
const loadingAgencies = ref(false)
const filteredCategories = ref([])
const filteredAgencies = ref([])
const voiceSupported = ref(false)
const autoSaveTimer = ref<NodeJS.Timeout>()

// Opciones de datos
const priorityOptions = [
  { title: 'Crítica', value: 'CRITICA', color: 'error' },
  { title: 'Alta', value: 'ALTA', color: 'warning' },
  { title: 'Media', value: 'MEDIA', color: 'info' },
  { title: 'Baja', value: 'BAJA', color: 'success' }
]

const categoryOptions = [
  { title: 'Tecnología', value: 'TECNOLOGIA', icon: 'mdi-laptop', color: 'blue' },
  { title: 'Personal', value: 'PERSONAL', icon: 'mdi-account-group', color: 'green' },
  { title: 'Infraestructura', value: 'INFRAESTRUCTURA', icon: 'mdi-building', color: 'orange' },
  { title: 'Seguridad', value: 'SEGURIDAD', icon: 'mdi-shield', color: 'red' },
  { title: 'Inventario', value: 'INVENTARIO', icon: 'mdi-package', color: 'purple' },
  { title: 'Otro', value: 'OTRO', icon: 'mdi-help-circle', color: 'grey' }
]

const techIssueTypes = [
  { title: 'Falla de Internet', value: 'INTERNET' },
  { title: 'Problema de PC/Laptop', value: 'COMPUTER' },
  { title: 'Falla de Impresora', value: 'PRINTER' },
  { title: 'Sistema de Ventas', value: 'SALES_SYSTEM' },
  { title: 'Problema Eléctrico', value: 'ELECTRICAL' }
]

const personalIssueTypes = [
  { title: 'Ausencia sin aviso', value: 'ABSENCE' },
  { title: 'Tardanza', value: 'LATE' },
  { title: 'Sin empleada fija', value: 'NO_EMPLOYEE' },
  { title: 'Cambio de turno', value: 'SHIFT_CHANGE' }
]

// Reglas de validación
const validationRules = {
  title: [
    { required: true, message: 'El título es requerido' },
    { minLength: 5, message: 'El título debe tener al menos 5 caracteres' },
    { maxLength: 100, message: 'El título no puede exceder 100 caracteres' }
  ],
  priority: [
    { required: true, message: 'La prioridad es requerida' }
  ],
  category: [
    { required: true, message: 'La categoría es requerida' }
  ],
  agencyId: [
    { required: true, message: 'La agencia afectada es requerida' }
  ],
  description: [
    { required: true, message: 'La descripción es requerida' },
    { minLength: 10, message: 'La descripción debe tener al menos 10 caracteres' },
    { maxLength: 500, message: 'La descripción no puede exceder 500 caracteres' }
  ]
}

// Computed
const formProgress = computed(() => {
  const requiredFields = ['title', 'priority', 'category', 'agencyId', 'description']
  const completedFields = requiredFields.filter(field => formData.value[field])
  return (completedFields.length / requiredFields.length) * 100
})

const showConditionalFields = computed(() => {
  return formData.value.category && ['TECNOLOGIA', 'PERSONAL'].includes(formData.value.category)
})

// Lifecycle
onMounted(() => {
  // Inicializar categorías y agencias
  filteredCategories.value = categoryOptions
  filteredAgencies.value = props.agencies

  // Verificar soporte de voz
  voiceSupported.value = 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window

  // Iniciar auto-guardado si está habilitado
  if (props.autoSave) {
    startAutoSave()
  }
})

// Watchers
watch(() => props.modelValue, (newValue) => {
  formData.value = { ...newValue }
}, { deep: true })

watch(formData, (newValue) => {
  emit('update:modelValue', newValue)
}, { deep: true })

// Métodos
const onValidationChange = (valid: boolean, errors: Record<string, string>) => {
  isFormValid.value = valid
}

const markTouched = (field: string) => {
  formValidator.value?.markFieldAsTouched(field)
}

const onCategorySearch = (search: string) => {
  if (!search) {
    filteredCategories.value = categoryOptions
    return
  }
  
  filteredCategories.value = categoryOptions.filter(cat =>
    cat.title.toLowerCase().includes(search.toLowerCase())
  )
}

const onAgencySearch = (search: string) => {
  if (!search) {
    filteredAgencies.value = props.agencies
    return
  }
  
  filteredAgencies.value = props.agencies.filter(agency =>
    agency.name.toLowerCase().includes(search.toLowerCase()) ||
    agency.code.toLowerCase().includes(search.toLowerCase())
  )
}

const onFileDrop = (event: DragEvent) => {
  isDragOver.value = false
  const files = Array.from(event.dataTransfer?.files || [])
  addFiles(files)
}

const onFileSelect = (event: Event) => {
  const target = event.target as HTMLInputElement
  const files = Array.from(target.files || [])
  addFiles(files)
}

const addFiles = (files: File[]) => {
  const validFiles = files.filter(file => {
    // Validar tipo y tamaño
    const validTypes = ['image/', 'application/pdf', 'application/msword']
    const isValidType = validTypes.some(type => file.type.startsWith(type))
    const isValidSize = file.size <= 10 * 1024 * 1024 // 10MB
    
    return isValidType && isValidSize
  })
  
  selectedFiles.value.push(...validFiles)
  formData.value.files = selectedFiles.value
}

const removeFile = (index: number) => {
  selectedFiles.value.splice(index, 1)
  formData.value.files = selectedFiles.value
}

const getFileIcon = (type: string) => {
  if (type.startsWith('image/')) return 'mdi-image'
  if (type.includes('pdf')) return 'mdi-file-pdf-box'
  if (type.includes('word')) return 'mdi-file-word-box'
  return 'mdi-file'
}

const getFileIconColor = (type: string) => {
  if (type.startsWith('image/')) return 'blue'
  if (type.includes('pdf')) return 'red'
  if (type.includes('word')) return 'info'
  return 'grey'
}

const formatFileSize = (bytes: number) => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

const startVoiceInput = () => {
  if (!voiceSupported.value) return
  
  const recognition = new (window.webkitSpeechRecognition || window.SpeechRecognition)()
  recognition.lang = 'es-ES'
  recognition.continuous = false
  recognition.interimResults = false
  
  recognition.onresult = (event) => {
    const transcript = event.results[0][0].transcript
    formData.value.description = (formData.value.description || '') + ' ' + transcript
  }
  
  recognition.start()
}

const handleSubmit = async () => {
  const isValid = await formValidator.value.validate()
  
  if (!isValid) {
    return
  }
  
  isSubmitting.value = true
  
  try {
    emit('submit', { ...formData.value })
  } finally {
    isSubmitting.value = false
  }
}

const handleCancel = () => {
  emit('cancel')
}

const saveAsDraft = async () => {
  isDraftSaving.value = true
  
  try {
    emit('save-draft', { ...formData.value })
    showAutoSaveIndicator.value = true
  } finally {
    isDraftSaving.value = false
  }
}

const startAutoSave = () => {
  if (autoSaveTimer.value) {
    clearInterval(autoSaveTimer.value)
  }
  
  autoSaveTimer.value = setInterval(() => {
    if (formProgress.value > 20 && !isSubmitting.value) {
      saveAsDraft()
    }
  }, props.autoSaveInterval)
}

// Cleanup
onUnmounted(() => {
  if (autoSaveTimer.value) {
    clearInterval(autoSaveTimer.value)
  }
})
</script>

<style scoped>
.smart-incident-form {
  max-width: 900px;
  margin: 0 auto;
}

.form-header {
  text-align: center;
}

.file-drop-zone {
  border: 2px dashed #ccc;
  border-radius: 8px;
  padding: 40px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: rgba(0, 0, 0, 0.02);
}

.file-drop-zone:hover,
.file-drop-zone.dragover {
  border-color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-primary), 0.05);
}

.form-actions {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  padding-top: 24px;
}
</style>
